import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Optional } from '@/utils'
import {
  OnChangeFilterDropdownEvent,
  OnChangeFilterDropdownMultiple,
  OnChangeFilterDropdownSingle,
} from '@/components/Data'
import { FilterDropdownResponsiveProps } from './FilterDropdownResponsive.types'

export const useFilterDropdownResponsiveController = <Options extends object>({
  label,
  value,
  options,
  valueKey,
  labelKey,
  onChange,
  multiple,
}: FilterDropdownResponsiveProps<Options>) => {
  const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value)

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [intermediateValue, setIntermediateValue] =
    useState<FilterDropdownResponsiveProps<Options>['value']>(value)
  const intermediateValueArray = intermediateValue as string[]

  // Narrow onChange into two callable shapes so TS knows which signature we're calling.
  const onChangeMultiple = onChange as Optional<OnChangeFilterDropdownMultiple<Options>>
  const onChangeSingle = onChange as Optional<OnChangeFilterDropdownSingle<Options>>

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false)
    setIntermediateValue(value)
  }, [value])

  // on apply filter out selected data and send to parent
  const onApply = useCallback(() => {
    if (multiple) {
      const selectedValues = options.filter((option) =>
        intermediateValueArray.includes(option[valueKey] as string),
      )
      onChangeMultiple?.(selectedValues as Options[], {} as OnChangeFilterDropdownEvent)
    } else {
      const selectedOption = options.find((option) => option[valueKey] === intermediateValue)
      onChangeSingle?.(selectedOption as Options, {} as OnChangeFilterDropdownEvent)
    }

    closeDrawer()
  }, [
    closeDrawer,
    intermediateValueArray,
    intermediateValue,
    multiple,
    onChangeMultiple,
    onChangeSingle,
    options,
    valueKey,
  ])

  const onRadioChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      const selectedOption = options.find((option) => option[valueKey] === newValue)
      setIntermediateValue(selectedOption?.[valueKey])
    },
    [options, valueKey],
  )

  const onCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name: id, checked } = event.target
      const isInValues = intermediateValueArray.includes(id)

      if (checked && !isInValues) setIntermediateValue((prev: string[]) => [...prev, id])
      else if (!checked && isInValues)
        setIntermediateValue((prev: string[]) => prev.filter((item) => item !== id))
    },
    [intermediateValueArray],
  )

  // sync value changes from parent
  useEffect(() => {
    setIntermediateValue(value)
  }, [value])

  const displayLabel = useMemo(() => {
    if (!hasValue) return label

    if (multiple && Array.isArray(value) && value.length > 0)
      return `${label?.toString()} (${value.length})`

    return options.find((option) => option[valueKey] === value)?.[labelKey] as string
  }, [hasValue, label, labelKey, multiple, options, value, valueKey])

  return {
    displayLabel,
    hasValue,
    isDrawerOpen,
    setIsDrawerOpen,
    closeDrawer,
    onApply,
    intermediateValue,
    intermediateValueArray,
    setIntermediateValue,
    onRadioChange,
    onCheckboxChange,
  }
}
